import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { CreatableCombobox } from "../../../components/ui/creatable-combobox";
import { toast } from "sonner";
import { fetchFastApi } from "../../../api/fastapi";
import { SetDefaultFyCycleDialog } from "../../../components/audit/SetDefaultFyCycleDialog";
import { DeleteSessionDialog } from "../../../components/audit/DeleteSessionDialog";
import {
  SessionStatusFilter,
  useControlFilterOptions,
  useCreateControlSession,
} from "../../../hooks/useControlsV2";
import {
  FieldworkSession,
  useFieldworkRows,
} from "../../../hooks/useFieldworkRows";
import { useWorkStepFilterOptions } from "../../../hooks/useWorkStepFilterOptions";
import { useDebounce } from "../../../hooks/useDebounce";
import { useFyCycleField } from "../../../hooks/useFyCycleField";
import { TestPhase, TestPhases } from "../../../models/audit/controlSession";
import { FieldworkFilterBar } from "./components/FieldworkFilterBar";
import { FieldworkUnifiedTable } from "./components/FieldworkUnifiedTable";
import { OptroAccessListNotice } from "../../../components/audit/optroAccess/OptroAccessListNotice";
import { StatsStrip } from "./components/StatsStrip";

/**
 * The unified Fieldwork page: controls and work steps in one list.
 *
 * Structurally a sibling of FieldworkPage, which stays on the flag-off path
 * rendering controls only. The duplication is deliberate — the flag-off path
 * must keep exactly today's behaviour — and FieldworkPage/FieldworkTable are
 * deleted with the flag.
 *
 * Unlike FieldworkPage this page takes no props: it is routed directly rather
 * than nested inside ControlsPageV2, so it fetches its own filter options and
 * renders its own header.
 */
export function FieldworkUnifiedPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { filterOptions } = useControlFilterOptions(false);
  const { filterOptions: workStepFilterOptions } = useWorkStepFilterOptions();

  // --- Filters, initialised from the URL ---------------------------------
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sessionStatusFilters, setSessionStatusFilters] = useState<
    SessionStatusFilter[]
  >(
    searchParams
      .getAll("sessionStatus")
      .filter(
        (v): v is SessionStatusFilter =>
          v === "in_progress" || v === "in_review" || v === "complete",
      ),
  );
  const [testPhaseFilters, setTestPhaseFilters] = useState<TestPhase[]>(
    searchParams
      .getAll("testPhase")
      .filter((v): v is TestPhase => TestPhases.some((tp) => tp.value === v)),
  );
  const [cycleFilters, setCycleFilters] = useState<string[]>(
    searchParams.getAll("cycle").filter(Boolean),
  );
  const [processFilters, setProcessFilters] = useState<string[]>(
    searchParams.getAll("process").filter(Boolean),
  );
  const [controlOwnerFilters, setControlOwnerFilters] = useState<string[]>(
    searchParams.getAll("controlOwner").filter(Boolean),
  );
  const [testCreatorFilters, setTestCreatorFilters] = useState<string[]>(
    searchParams.getAll("testCreator").filter(Boolean),
  );
  const [fyCycleIdFilters, setFyCycleIdFilters] = useState<string[]>(
    searchParams.getAll("fyCycleId").filter(Boolean),
  );
  const [sectionFilters, setSectionFilters] = useState<string[]>(
    searchParams.getAll("section").filter(Boolean),
  );
  const [subsectionFilters, setSubsectionFilters] = useState<string[]>(
    searchParams.getAll("subsection").filter(Boolean),
  );
  const [preparerFilters, setPreparerFilters] = useState<string[]>(
    searchParams.getAll("preparer").filter(Boolean),
  );
  const [opsAuditIdFilters, setOpsAuditIdFilters] = useState<string[]>(
    searchParams.getAll("opsAuditId").filter(Boolean),
  );
  // Defaults to "name"/"asc", not ""/"desc": the hook always sends
  // sort_by="name" (the backend requires a name window to merge controls and
  // work steps), and the first page is fetched descending unless told
  // otherwise. Seeding the neutral state to match what actually goes over the
  // wire keeps the Name column's sort indicator honest on first load instead
  // of showing "unsorted" while the rows render Z→A.
  const [sortBy, setSortBy] = useState<string>(
    searchParams.get("sortBy") || "name",
  );
  // Validated, not asserted: sortOrder comes straight off the URL, so a cast
  // would hand `?sortOrder=ASC` to the API as a well-typed value and let the
  // backend silently return nothing. Anything but "asc"/"desc" degrades to
  // the default.
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(() => {
    const raw = searchParams.get("sortOrder");
    return raw === "asc" || raw === "desc" ? raw : "asc";
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // --- Data ---------------------------------------------------------------
  // useFieldworkRows serialises this object itself (JSON.stringify) to decide
  // when to refetch, so it does not need to be memoised here.
  const {
    rows,
    totalCount,
    sessionSummary,
    optroAccessUnavailable,
    loading,
    hasMore,
    loadMore,
    refetch,
  } = useFieldworkRows({
    searchTerm: debouncedSearch,
    sessionStatuses: sessionStatusFilters,
    testCreators: testCreatorFilters,
    cycles: cycleFilters,
    processes: processFilters,
    controlOwners: controlOwnerFilters,
    testPhases: testPhaseFilters,
    fyCycleIds: fyCycleIdFilters,
    sections: sectionFilters,
    subsections: subsectionFilters,
    preparers: preparerFilters,
    opsAuditIds: opsAuditIdFilters,
    sortBy,
    sortOrder,
  });

  // --- Delete session dialog (controls only) ------------------------------
  // Copied from FieldworkPage, which this page deliberately duplicates.
  const [sessionToDelete, setSessionToDelete] =
    useState<FieldworkSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/controls/v2/control-sessions/${sessionToDelete.id}`,
      });
      toast.success("Test deleted");
      setSessionToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete session:", error);
      toast.error("Failed to delete test");
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Create session dialog (controls only) ------------------------------
  const [createSessionControlId, setCreateSessionControlId] = useState<
    string | null
  >(null);
  const [newSessionName, setNewSessionName] = useState("New Test");
  const [newSessionTestPhase, setNewSessionTestPhase] =
    useState<TestPhase>("not_defined");
  const { createSession, loading: loadingCreateSession } =
    useCreateControlSession();
  const {
    fyCycle: newSessionFyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    initWithDefault,
    handleChange: handleFyCycleChange,
    confirmSetDefault,
    dismissSetDefault,
    resolveFyCycleId,
    reset: resetFyCycle,
  } = useFyCycleField();

  // Sync every filter to the URL. No `tab` param to preserve: unlike
  // FieldworkPage this page is not a tab inside ControlsPageV2.
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    sessionStatusFilters.forEach((v) => params.append("sessionStatus", v));
    testPhaseFilters.forEach((v) => params.append("testPhase", v));
    cycleFilters.forEach((v) => params.append("cycle", v));
    processFilters.forEach((v) => params.append("process", v));
    controlOwnerFilters.forEach((v) => params.append("controlOwner", v));
    testCreatorFilters.forEach((v) => params.append("testCreator", v));
    fyCycleIdFilters.forEach((v) => params.append("fyCycleId", v));
    sectionFilters.forEach((v) => params.append("section", v));
    subsectionFilters.forEach((v) => params.append("subsection", v));
    preparerFilters.forEach((v) => params.append("preparer", v));
    opsAuditIdFilters.forEach((v) => params.append("opsAuditId", v));
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchTerm,
    sessionStatusFilters,
    testPhaseFilters,
    cycleFilters,
    processFilters,
    controlOwnerFilters,
    testCreatorFilters,
    fyCycleIdFilters,
    sectionFilters,
    subsectionFilters,
    preparerFilters,
    opsAuditIdFilters,
    sortBy,
    sortOrder,
    searchParams,
    setSearchParams,
  ]);

  // Infinite scroll sentinel — disconnect on unmount to prevent leaks
  const observer = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) loadMore();
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore],
  );

  const hasActiveFilters =
    !!searchTerm ||
    sessionStatusFilters.length > 0 ||
    testPhaseFilters.length > 0 ||
    cycleFilters.length > 0 ||
    processFilters.length > 0 ||
    controlOwnerFilters.length > 0 ||
    testCreatorFilters.length > 0 ||
    fyCycleIdFilters.length > 0 ||
    sectionFilters.length > 0 ||
    subsectionFilters.length > 0 ||
    preparerFilters.length > 0 ||
    opsAuditIdFilters.length > 0;

  const activeFilterCount = [
    sessionStatusFilters.length > 0,
    testPhaseFilters.length > 0,
    cycleFilters.length > 0,
    processFilters.length > 0,
    controlOwnerFilters.length > 0,
    testCreatorFilters.length > 0,
    fyCycleIdFilters.length > 0,
    sectionFilters.length > 0,
    subsectionFilters.length > 0,
    preparerFilters.length > 0,
    opsAuditIdFilters.length > 0,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm("");
    setSessionStatusFilters([]);
    setTestPhaseFilters([]);
    setCycleFilters([]);
    setProcessFilters([]);
    setControlOwnerFilters([]);
    setTestCreatorFilters([]);
    setFyCycleIdFilters([]);
    setSectionFilters([]);
    setSubsectionFilters([]);
    setPreparerFilters([]);
    setOpsAuditIdFilters([]);
  };

  const handleCreateSession = async () => {
    if (!createSessionControlId) return;
    try {
      const fyCycleId = await resolveFyCycleId();
      const sessionId = await createSession(
        createSessionControlId,
        newSessionName,
        newSessionTestPhase,
        fyCycleId,
      );
      if (sessionId) {
        setCreateSessionControlId(null);
        setNewSessionName("New Test");
        setNewSessionTestPhase("not_defined");
        resetFyCycle();
        navigate(`/audit/control-sessions/session/${sessionId}`);
      }
    } catch {
      // resolveFyCycleId / createSession have already toasted; keep dialog open.
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 px-6 pt-6">
        <h1 className="text-3xl font-bold tracking-tight">Fieldwork</h1>
        <p className="text-stone-600">
          Track testing across your controls and work steps
        </p>
      </div>

      <StatsStrip
        sessionSummary={sessionSummary}
        totalCount={totalCount}
        totalCountLabel="controls & work steps"
        sessionStatusFilters={sessionStatusFilters}
        onSessionStatusFiltersChange={setSessionStatusFilters}
      />

      {/* Filter bar */}
      <div className="shrink-0 px-6 py-3 border-b border-stone-200">
        <FieldworkFilterBar
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          testPhaseFilters={testPhaseFilters}
          onTestPhaseFiltersChange={setTestPhaseFilters}
          cycleFilters={cycleFilters}
          onCycleFiltersChange={setCycleFilters}
          processFilters={processFilters}
          onProcessFiltersChange={setProcessFilters}
          controlOwnerFilters={controlOwnerFilters}
          onControlOwnerFiltersChange={setControlOwnerFilters}
          sessionStatusFilters={sessionStatusFilters}
          onSessionStatusFiltersChange={setSessionStatusFilters}
          testCreatorFilters={testCreatorFilters}
          onTestCreatorFiltersChange={setTestCreatorFilters}
          fyCycleIdFilters={fyCycleIdFilters}
          onFyCycleIdFiltersChange={setFyCycleIdFilters}
          filterOptions={filterOptions}
          workStepFilterOptions={workStepFilterOptions}
          sectionFilters={sectionFilters}
          onSectionFiltersChange={setSectionFilters}
          subsectionFilters={subsectionFilters}
          onSubsectionFiltersChange={setSubsectionFilters}
          preparerFilters={preparerFilters}
          onPreparerFiltersChange={setPreparerFilters}
          opsAuditIdFilters={opsAuditIdFilters}
          onOpsAuditIdFiltersChange={setOpsAuditIdFilters}
          isFiltersOpen={isFiltersOpen}
          onFiltersOpenChange={setIsFiltersOpen}
          activeFilterCount={activeFilterCount}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          searchPlaceholder="Search tests by control, work step, name, or system..."
        />
      </div>

      <OptroAccessListNotice
        unavailable={optroAccessUnavailable}
        onRetry={refetch}
        className="mx-6 mt-4 w-auto shrink-0"
      />

      {/* Scrollable table area */}
      <div className="flex-1 overflow-auto px-6 mt-4 pb-16">
        <FieldworkUnifiedTable
          rows={rows}
          loading={loading}
          hasActiveFilters={hasActiveFilters}
          lastRowRef={lastRowRef}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(field) => {
            if (sortBy === field) {
              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
            } else {
              setSortBy(field);
              setSortOrder("desc");
            }
          }}
          onCreateSession={async (rowId) => {
            setCreateSessionControlId(rowId);
            setNewSessionName("New Test");
            setNewSessionTestPhase("not_defined");
            await initWithDefault();
          }}
          onDeleteSession={setSessionToDelete}
        />
      </div>

      {/* Delete session dialog — controls only, like the create dialog. */}
      <DeleteSessionDialog
        open={Boolean(sessionToDelete)}
        onOpenChange={(open) => {
          if (!open) setSessionToDelete(null);
        }}
        isDeleting={isDeleting}
        sessionName={sessionToDelete?.name}
        onConfirm={handleConfirmDelete}
      />

      {/* Create session dialog — controls only; the create-session endpoint
          has no work-step equivalent, so FieldworkUnifiedTable renders the
          trigger for control rows only. */}
      <Dialog
        open={Boolean(createSessionControlId)}
        onOpenChange={(open) => {
          if (!open) setCreateSessionControlId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new test</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="session-name" className="text-sm font-medium">
              Test name
            </Label>
            <Input
              id="session-name"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              className="mt-1.5"
              placeholder="e.g. Q4 2024 Test"
            />
            <Label htmlFor="testPhase" className="text-sm font-medium mt-2">
              Test Phase
            </Label>
            <Select
              value={newSessionTestPhase}
              onValueChange={(value) => {
                const phase = TestPhases.find((tp) => tp.value === value);
                if (phase) setNewSessionTestPhase(phase.value);
              }}
            >
              <SelectTrigger id="testPhase">
                <SelectValue placeholder="Select test phase" />
              </SelectTrigger>
              <SelectContent>
                {TestPhases.map((phase) => (
                  <SelectItem key={phase.value} value={phase.value}>
                    {phase.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label htmlFor="fyCycle" className="text-sm font-medium mt-2">
              FY
            </Label>
            <CreatableCombobox
              value={newSessionFyCycle}
              onChange={handleFyCycleChange}
              options={fyCycleOptionNames}
              placeholder="Select or create FY cycle..."
              emptyText="No FY cycles yet. Type to create one."
              createText="Create"
              modal
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateSessionControlId(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSession}
              disabled={!newSessionName.trim() || loadingCreateSession}
            >
              {loadingCreateSession ? "Creating…" : "Create test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Default FY Cycle Dialog */}
      <SetDefaultFyCycleDialog
        open={pendingDefaultName !== null}
        optionName={pendingDefaultName ?? ""}
        onConfirm={confirmSetDefault}
        onDismiss={dismissSetDefault}
      />
    </div>
  );
}
